import React, { lazy, useEffect, useMemo, useRef } from "react";
import { CarDataType, Cars } from "data/types";
import { DisplayType } from "routers/types";
import { useDisplay } from "hooks/useDisplay";
import { useObservedQuery } from "hooks/useObservedQuery";

import CarCard from "components/CarCard/CarCard";
import CarCardList from "components/CarCard/CarCardList";

import ButtonCircle from "shared/Button/ButtonCircle";
import CarCardSkeleton from "components/CarCard/CarSkeleton";
import { convertToNumber, getLikedCars } from "utils/utils";
import { SortFilters } from "./SortFilters";
import { useLocation } from "react-router-dom";

// import { FixedSizeGrid as Grid } from 'react-window';
// import { FixedSizeList as List } from 'react-window';

import noResults from "images/no-results.webp";
import { SectionSocials } from "components/SectionSubscribe2/SectionSocials";

const Loading = lazy(() => import("components/Loading/Loading"));
const NcImage = lazy(() => import("shared/NcImage/NcImage"));

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const CarsComponent: React.FC<SectionGridFilterCardProps> = () => {
  const { display, setDisplay, getPersistedDisplay } = useDisplay();
  const { data, isLoading, error, isFetching, isFetchingNextPage, updateFilters, sortOptions, fetchNextPage, hasNextPage } = useObservedQuery();

  const observerRef = useRef<HTMLDivElement | null>(null);

  // get location and url params
  const location = useLocation();

  // Infinite scroll
  useEffect(() => {
    let fetching = false;
    const onScroll = async (event: any) => {
      if (window.location.pathname !== '/cars') return;
      const { scrollHeight, scrollTop, clientHeight } =
        event.target.scrollingElement;

      if (!fetching && scrollHeight - scrollTop <= clientHeight * 3.3) {
        fetching = true;
        if (hasNextPage) await fetchNextPage();
        fetching = false;
      }
    };

    document.addEventListener("scroll", onScroll);
    return () => {
      document.removeEventListener("scroll", onScroll);
    };
  }, [hasNextPage]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("make");
    if (tab) {
      updateFilters(
        {
          ...sortOptions!,
          keywords: undefined,
          sort: sortOptions.sort === "myFavourites" ? "totalPriceAsc" : sortOptions.sort,
          standardMake: [tab]
        }
      )
    }
  }, [location])

  // on window size change, change display type
  useEffect(() => {
    if (window.innerWidth < 768) {
      setDisplay(DisplayType.GRID_SM, false)
    }

    if (window.innerWidth >= 768) {
      setDisplay(getPersistedDisplay())
    }
  }, [window.innerWidth])

  const sortData = (data: { pages: [{ results: Cars[] }] }) => {
    if (!data || !data.pages) return data;

    // sort data by sort option
    const sortOption = sortOptions?.sort;

    switch (sortOption) {
      case "vehicleAgeDesc":
        data.pages[0].results?.sort((a, b) => {
          if (!a?.firstRegistrationDate) return 1;
          if (!b?.firstRegistrationDate) return -1;

          return new Date(b.firstRegistrationDate).getTime() - new Date(a.firstRegistrationDate).getTime();
        })
        break;
      case "vehicleAgeAsc":
        data.pages[0].results?.sort((a, b) => {
          if (!a?.firstRegistrationDate) return 1;
          if (!b?.firstRegistrationDate) return -1;

          return new Date(a.firstRegistrationDate).getTime() - new Date(b.firstRegistrationDate).getTime();
        })
        break;
      case "totalPriceDesc":
        data.pages?.map((page: any) => {
          return page.results?.sort((a: any, b: any) => {
            if (!a?.priceGBP) return 1;
            if (!b?.priceGBP) return -1;

            return convertToNumber(b.priceGBP) - convertToNumber(a.priceGBP);
          })
        })
        break;
      case "totalPriceAsc":
        data.pages?.map((page: any) => {
          return page.results?.sort((a: any, b: any) => {
            if (!a?.priceGBP) return 1;
            if (!b?.priceGBP) return -1;

            return convertToNumber(a.priceGBP) - convertToNumber(b.priceGBP);
          })
        })
        break;
      case "odometerReadingMilesDesc":
        data.pages.map((page: any) => {
          return page.results.sort((a: any, b: any) => {
            if (!a?.mileage) return 1;
            if (!b?.mileage) return -1;

            return b.mileage - a.mileage;
          })
        })
        break;
      case "odometerReadingMilesAsc":
        data.pages.map((page: any) => {
          return page.results.sort((a: any, b: any) => {
            if (!a?.mileage) return 1;
            if (!b?.mileage) return -1;

            return a.mileage - b.mileage;
          })
        })
        break;
    }

    return data;
  }

  const sortedData = useMemo(() => sortData(data), [data]);

  const memoizedDisplayCars = useMemo(() => {
    if (!sortedData || !sortedData.pages) return null;

    const likedCars = getLikedCars();

    return sortedData.pages.flatMap((page: any) =>
      page.results?.map((item: any) => {
        const isLiked = likedCars.includes(item.meta.carId);
        const cardProps = { key: item.meta.carId, data: item, isLiked };

        return display === DisplayType.LIST ? (
          <div {...cardProps}>
            <CarCardList {...cardProps} />
          </div>
        ) : (
          <CarCard
            {...cardProps}
            size={
              display === DisplayType.GRID_LG ? 'default' :
                display === DisplayType.GRID_SM ? 'small' : 'xsmall'
            }
          />
        );
      })
    );
  }, [display, sortedData, getLikedCars]);

  const renderSkeleton = () => {
    let skeleton = [];
    for (let i = 0; i < 8; i++) skeleton.push(<CarCardSkeleton key={i} displayType={display} />)
    return skeleton;
  }

  const getGridClass = () => {
    switch (display) {
      case DisplayType.GRID_LG:
        return "grid grid-cols-1 md:grid-cols-2 md:gap-8 sm:grid-cols-2 lg:grid-cols-3"
      case DisplayType.GRID_SM:
        return "grid grid-cols-1 md:grid-cols-2 md:gap-8 sm:grid-cols-2 lg:grid-cols-2"
      case DisplayType.GRID_XS:
        return "grid grid-cols-1 md:grid-cols-3 md:gap-8 sm:grid-cols-2 lg:grid-cols-4"
      case DisplayType.LIST:
        return "flex flex-col md:gap-8"
      // return "grid grid-cols-1 md:grid-cols-2 md:gap-8 sm:grid-cols-2 lg:grid-cols-4"
      default:
        return "grid grid-cols-1 md:grid-cols-2 md:gap-8 sm:grid-cols-2 lg:grid-cols-3"
    }
  }

  // Memoize expensive functions
  const memoizedSkeleton = useMemo(renderSkeleton, [display]);
  const memoizedGridClass = useMemo(getGridClass, [display]);

  return (
    <div className={`relative overflow-x-hidden pt-5 dark:bg-custom-900`}>
      <div className="container relative">
        <div data-nc-id="SectionGridFilterCard">
          <span className="text-neutral-500 dark:text-white hidden lg:flex">
            <span className="text-xs my-3 ml-2 uppercase">{data && `${data.pages[0].totalResults} result${data.pages[0].totalResults > 1 ? 's' : ''}`} </span>
          </span>

          <div className="w-full flex justify-between mb-8 lg:mb-11 flex-row">
            {/* Display type */}
            <div className="hidden gap-3 lg:flex">
              <ButtonCircle
                className={`hover:bg-transparent px-1 
                ${display === DisplayType.LIST ? "text-black dark:text-white" : "text-gray-400 dark:text-custom-200"}`}
                onClick={() => setDisplay(DisplayType.LIST)}>
                <span className={`text-sm  mt-1 font-medium ${display === DisplayType.LIST ? "text-black dark:text-red-600" : "text-gray-400 dark:text-custom-200"}`}>
                  <i className="text-xl far fa-list"></i>
                </span>
              </ButtonCircle>
              <ButtonCircle
                className={`hover:bg-transparent px-1
                ${display === DisplayType.GRID_SM ? "text-black dark:text-white" : "text-gray-400 dark:text-custom-200"}`}
                onClick={() => setDisplay(DisplayType.GRID_SM)}>
                <span className={`text-sm  mt-1 font-medium ${display === DisplayType.GRID_SM ? "text-black dark:text-red-600" : "text-gray-400 dark:text-custom-200"}`}>
                  <i className="text-xl far fa-grid-2"></i>
                </span>
              </ButtonCircle>
              <ButtonCircle
                className={`hover:bg-transparent  px-1
                ${display === DisplayType.GRID_LG ? "text-black dark:text-white" : "text-gray-400 dark:text-custom-200"}`}
                onClick={() => setDisplay(DisplayType.GRID_LG)}>
                <span className={`text-sm  mt-1 font-medium ${display === DisplayType.GRID_LG ? "text-black dark:text-red-600" : "text-gray-400 dark:text-custom-200"}`}>
                  <i className="text-xl far fa-grid-3"></i>
                </span>
              </ButtonCircle>
              <ButtonCircle
                className={`hover:bg-transparent  px-1
                ${display === DisplayType.GRID_XS ? "text-black dark:text-white" : "text-gray-400 dark:text-custom-200"}`}
                onClick={() => setDisplay(DisplayType.GRID_XS)}>
                <span className={`text-sm  mt-1 font-medium ${display === DisplayType.GRID_XS ? "text-black dark:text-red-600" : "text-gray-400 dark:text-custom-200"}`}>
                  <i className="text-xl far fa-grid-4"></i>
                </span>
              </ButtonCircle>
            </div>

            <span className="text-neutral-500 dark:text-white flex lg:hidden">
              <span className="text-xs my-3 ml-2 uppercase">{data && `${data.pages[0].totalResults} result${data.pages[0].totalResults > 1 ? 's' : ''}`} </span>
            </span>
            {!isLoading && (
              <SortFilters />
            )}
          </div>

          <div className={`gap-6 ${memoizedGridClass}`} ref={observerRef}>
            {(isLoading || isFetching) && !isFetchingNextPage ? memoizedSkeleton : null}
            {
              error instanceof Error ?
                (
                  <div className="flex flex-col items-center justify-center mt-10 w-full col-span-full">
                    <NcImage
                      containerClassName="w-[700px] max-w-full mx-auto"
                      src={noResults}
                      alt="error"
                    />
                    <span className="mt-5 text-xl font-display text-center font-extrabold">Please try <button
                      className=" text-red-500 hover:text-red-600"
                      onClick={() => {
                        updateFilters({
                          sort: 'totalPriceDesc',
                        })
                      }}
                    >
                      clearing all filters
                    </button></span>

                  </div>
                ) :
                (!isLoading || !isFetching) && memoizedDisplayCars
            }
          </div>

          {data && data.pages && data.pages[0].totalResults === 0 && (
            <div className="flex flex-col items-center justify-center mt-10">
              <NcImage
                containerClassName="w-[700px] max-w-full mx-auto"
                src={noResults}
                alt="empty"
              />
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center mt-14">
              {/* <div className="w-10 h-10 border-t-2 border-b-2 border-red-500 rounded-full animate-spin"></div> */}
              <Loading className="" loaderDimensions={
                {
                  height: 80,
                  width: 80
                }
              } />
            </div>
          )}
        </div>
      </div>

      <div className="mt-24">
        <SectionSocials />
      </div>
    </div>
  );
};

export default React.memo(CarsComponent);
